import React from 'react';
import { Box, Typography } from '@mui/material';
import { useImportWizardStore } from '../../store/useImportWizardStore';
import ColumnSelect from './ColumnSelect';

/**
 * Address field column mapping selectors.
 * Only rendered when importMode === 'deals_with_addresses'.
 * - Required: latitude, longitude
 * - Optional: street, city, region, country, postalCode
 *
 * Validates: Requirements 7.3, 7.4
 */
const AddressFieldMapping: React.FC = () => {
  const headers = useImportWizardStore((s) => s.headers);
  const columnMappings = useImportWizardStore(
    (s) => s.mappingConfig.columnMappings
  );
  const updateMappingConfig = useImportWizardStore(
    (s) => s.updateMappingConfig
  );

  const addressFields = columnMappings?.addressFields ?? {
    latitude: '',
    longitude: '',
  };

  const handleChange = (field: string, value: string) => {
    const updatedAddressFields = {
      ...addressFields,
      [field]: value || undefined,
    };

    if (field === 'latitude') updatedAddressFields.latitude = value;
    if (field === 'longitude') updatedAddressFields.longitude = value;

    updateMappingConfig({
      columnMappings: {
        dealFields: columnMappings?.dealFields ?? { extID: '' },
        ...columnMappings,
        addressFields: updatedAddressFields as {
          latitude: string;
          longitude: string;
          street?: string;
          city?: string;
          region?: string;
          country?: string;
          postalCode?: string;
        },
      },
    });
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Address Field Mapping
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <ColumnSelect
          label="Latitude"
          value={addressFields.latitude || ''}
          headers={headers}
          onChange={(v) => handleChange('latitude', v)}
          required
          data-testid="address-latitude-select"
        />

        <ColumnSelect
          label="Longitude"
          value={addressFields.longitude || ''}
          headers={headers}
          onChange={(v) => handleChange('longitude', v)}
          required
          data-testid="address-longitude-select"
        />

        <ColumnSelect
          label="Street"
          value={addressFields.street || ''}
          headers={headers}
          onChange={(v) => handleChange('street', v)}
          data-testid="address-street-select"
        />

        <ColumnSelect
          label="City"
          value={addressFields.city || ''}
          headers={headers}
          onChange={(v) => handleChange('city', v)}
          data-testid="address-city-select"
        />

        <ColumnSelect
          label="Region"
          value={addressFields.region || ''}
          headers={headers}
          onChange={(v) => handleChange('region', v)}
          data-testid="address-region-select"
        />

        <ColumnSelect
          label="Country"
          value={addressFields.country || ''}
          headers={headers}
          onChange={(v) => handleChange('country', v)}
          data-testid="address-country-select"
        />

        <ColumnSelect
          label="Postal Code"
          value={addressFields.postalCode || ''}
          headers={headers}
          onChange={(v) => handleChange('postalCode', v)}
          data-testid="address-postalcode-select"
        />
      </Box>
    </Box>
  );
};

export default AddressFieldMapping;
